// RefrigerantLog — EPA 608 compliance ledger for HVAC contractors.
// Tech-friendly capture form (phone or desktop) + running totals per
// refrigerant type + CSV export for record retention.
import { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Snowflake, FloppyDisk, DownloadSimple, Trash, Plus } from "@phosphor-icons/react";

import { todayLocalISO as todayISO } from "@/lib/dateHelpers";

const ACTION_LABELS = {
  added: "Added / Charged",
  recovered: "Recovered",
  leaked: "Leaked",
  topped_off: "Topped Off",
};
const ACTION_COLORS = {
  added: "bg-emerald-100 text-emerald-800",
  recovered: "bg-cyan-100 text-cyan-800",
  leaked: "bg-red-100 text-red-800",
  topped_off: "bg-amber-100 text-amber-800",
};

const initialForm = {
  entry_date: todayISO(),
  tech_name: "",
  tech_cert_number: "",
  tech_cert_class: "Type II",
  equipment_id: "",
  site_address: "",
  refrigerant_type: "R-410A",
  action: "added",
  lbs: "",
  reason: "",
  equipment_install_date: "",
  warranty_years: "",
};

export default function RefrigerantLog() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [equipWarranty, setEquipWarranty] = useState(null); // shows known-equipment warranty in the form

  const load = async () => {
    const r = await api.get("/hvac/refrigerant-log");
    setData(r.data);
  };
  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // On blur of the equipment_id field, auto-fill install date + warranty
  // if we've seen this unit before. This is what turns single-entry data
  // into a real warranty tracker.
  const lookupEquipment = async () => {
    const id = form.equipment_id.trim();
    if (!id) { setEquipWarranty(null); return; }
    try {
      const r = await api.get(`/hvac/equipment/${encodeURIComponent(id)}`);
      if (r.data.found) {
        const eq = r.data.equipment;
        setEquipWarranty(eq.warranty || null);
        setForm(prev => ({
          ...prev,
          equipment_install_date: prev.equipment_install_date || eq.install_date || "",
          warranty_years: prev.warranty_years || (eq.warranty_years ?? ""),
          site_address: prev.site_address || eq.site_address || "",
        }));
      } else {
        setEquipWarranty(null);
      }
    } catch { /* noop */ }
  };

  const save = async () => {
    if (!form.tech_name || !form.equipment_id || !form.lbs) {
      toast.error("Tech name, equipment ID, and lbs are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/hvac/refrigerant-log", {
        ...form,
        lbs: parseFloat(form.lbs),
        warranty_years: form.warranty_years ? parseInt(form.warranty_years, 10) : null,
        equipment_install_date: form.equipment_install_date || null,
      });
      toast.success("Entry logged");
      setForm({ ...initialForm, tech_name: form.tech_name,
                tech_cert_number: form.tech_cert_number,
                tech_cert_class: form.tech_cert_class });
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry? EPA requires 3-year retention.")) return;
    await api.delete(`/hvac/refrigerant-log/${id}`);
    await load();
  };

  const exportCSV = () => {
    const token = localStorage.getItem("access_token");
    window.open(`${API}/hvac/refrigerant-log/export.csv?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  if (!data) return <div className="p-6 text-slate-500">Loading log…</div>;

  return (
    <div className="p-4 md:p-6" data-testid="refrigerant-log-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#0f172a] text-white flex items-center justify-center">
            <Snowflake weight="fill" size={22} className="text-cyan-400"/>
          </div>
          <div>
            <h1 className="font-display font-black text-3xl leading-none">Refrigerant Log</h1>
            <div className="text-xs uppercase font-black tracking-widest text-slate-500 mt-1">
              EPA Section 608 · <span className="text-red-500">3-year retention</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCSV} data-testid="refrig-export-csv"
            className="h-11 px-4 border-2 border-slate-300 hover:bg-slate-100 text-slate-700 font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2">
            <DownloadSimple weight="bold" size={16}/> Export CSV
          </button>
          <button onClick={() => setShowForm(v => !v)} data-testid="refrig-add-btn"
            className="h-11 px-5 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg">
            <Plus weight="bold" size={16}/> {showForm ? "Cancel" : "New Entry"}
          </button>
        </div>
      </div>

      {/* Totals ledger */}
      {Object.keys(data.totals || {}).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="refrig-totals">
          {Object.entries(data.totals).map(([rt, t]) => (
            <div key={rt} className="bg-white border border-slate-200 rounded-xl p-3">
              <div className="font-black text-sm">{rt}</div>
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mt-1 leading-relaxed">
                <div>Added: <span className="text-emerald-600">{t.added?.toFixed(2) || "0.00"} lb</span></div>
                <div>Recovered: <span className="text-cyan-600">{t.recovered?.toFixed(2) || "0.00"} lb</span></div>
                {(t.leaked || 0) > 0 && <div>Leaked: <span className="text-red-600">{t.leaked?.toFixed(2)} lb</span></div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Entry form */}
      {showForm && (
        <div className="bg-white border-2 border-[#00C4CC] rounded-2xl p-4 md:p-5 mb-5 shadow-lg" data-testid="refrig-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Date">
              <input type="date" value={form.entry_date} onChange={e => setF("entry_date", e.target.value)}
                data-testid="refrig-date" className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
            </Field>
            <Field label="Tech Name *">
              <input value={form.tech_name} onChange={e => setF("tech_name", e.target.value)} placeholder="J. Smith"
                data-testid="refrig-tech" className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
            </Field>
            <Field label="Tech EPA Cert #">
              <input value={form.tech_cert_number} onChange={e => setF("tech_cert_number", e.target.value)} placeholder="608-12345"
                data-testid="refrig-cert" className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
            </Field>
            <Field label="Cert Class">
              <select value={form.tech_cert_class} onChange={e => setF("tech_cert_class", e.target.value)}
                data-testid="refrig-cert-class" className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium">
                {(data.cert_classes || []).map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Equipment ID / Serial *">
              <input value={form.equipment_id}
                onChange={e => setF("equipment_id", e.target.value)}
                onBlur={lookupEquipment}
                placeholder="AC-Unit-42 or serial"
                data-testid="refrig-equipment" className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
            </Field>
            <Field label="Site Address">
              <input value={form.site_address} onChange={e => setF("site_address", e.target.value)} placeholder="123 Main St"
                data-testid="refrig-site" className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
            </Field>
            <Field label="Refrigerant *">
              <select value={form.refrigerant_type} onChange={e => setF("refrigerant_type", e.target.value)}
                data-testid="refrig-type" className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium">
                {(data.refrigerants || []).map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Action *">
              <select value={form.action} onChange={e => setF("action", e.target.value)}
                data-testid="refrig-action" className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium">
                {(data.actions || []).map(c => <option key={c} value={c}>{ACTION_LABELS[c] || c}</option>)}
              </select>
            </Field>
            <Field label="Pounds *">
              <input type="number" step="0.01" min="0" value={form.lbs} onChange={e => setF("lbs", e.target.value)} placeholder="2.5"
                data-testid="refrig-lbs" className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
            </Field>
            <Field label="Reason / Notes">
              <input value={form.reason} onChange={e => setF("reason", e.target.value)} placeholder="Low charge diagnostic, leak repaired"
                data-testid="refrig-reason" className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
            </Field>
            <Field label="Unit Install Date">
              <input type="date" value={form.equipment_install_date}
                onChange={e => setF("equipment_install_date", e.target.value)}
                data-testid="refrig-install-date"
                className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
            </Field>
            <Field label="Warranty (years)">
              <input type="number" min="0" max="25" value={form.warranty_years}
                onChange={e => setF("warranty_years", e.target.value)}
                placeholder="10"
                data-testid="refrig-warranty-years"
                className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
            </Field>
          </div>
          {equipWarranty?.has_warranty && (
            <div data-testid="refrig-warranty-banner"
              className={`mt-3 px-4 py-2.5 rounded-lg text-sm font-bold border-l-4 ${
                equipWarranty.state === "expired" ? "bg-red-50 text-red-800 border-red-500" :
                equipWarranty.state === "expiring_soon" ? "bg-amber-50 text-amber-800 border-amber-500" :
                "bg-emerald-50 text-emerald-800 border-emerald-500"
              }`}>
              {equipWarranty.state === "expired"
                ? `⚠ Warranty EXPIRED on ${equipWarranty.expires_on} (${Math.abs(equipWarranty.days_left)} days ago) — bill parts + labor.`
                : equipWarranty.state === "expiring_soon"
                ? `⏰ Warranty expiring soon: ${equipWarranty.expires_on} (${equipWarranty.days_left} days left). Consider extended plan.`
                : `✓ Under warranty until ${equipWarranty.expires_on} (${equipWarranty.days_left} days left). Parts likely covered.`}
            </div>
          )}
          <div className="mt-4 flex items-center gap-3">
            <button onClick={save} disabled={saving} data-testid="refrig-save-btn"
              className="h-11 px-6 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 disabled:opacity-50 shadow">
              <FloppyDisk weight="bold" size={16}/> {saving ? "Saving…" : "Save Entry"}
            </button>
            <div className="text-[11px] text-slate-500">EPA 608 requires an entry for every transaction ≥ 1 lb.</div>
          </div>
        </div>
      )}

      {/* Ledger */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-600">
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Tech</div>
          <div className="col-span-2">Equipment</div>
          <div className="col-span-1">Refrigerant</div>
          <div className="col-span-2">Action</div>
          <div className="col-span-1 text-right">Lbs</div>
          <div className="col-span-2">Reason</div>
        </div>
        {data.entries.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="refrig-empty">
            No entries yet. Tap <b>New Entry</b> to log your first refrigerant transaction.
          </div>
        ) : data.entries.map(e => (
          <div key={e.id} data-testid={`refrig-row-${e.id}`}
            className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-3 border-b border-slate-100 items-center text-sm hover:bg-slate-50 transition-colors">
            <div className="md:col-span-2 font-mono text-xs">{e.entry_date}</div>
            <div className="md:col-span-2">
              <div className="font-bold text-sm">{e.tech_name}</div>
              {e.tech_cert_number && <div className="text-[10px] text-slate-500">Cert {e.tech_cert_number}</div>}
            </div>
            <div className="md:col-span-2">
              <div className="font-mono text-xs">{e.equipment_id}</div>
              {e.site_address && <div className="text-[10px] text-slate-500 truncate">{e.site_address}</div>}
              {e.warranty?.has_warranty && (
                <div data-testid={`refrig-warranty-pill-${e.id}`}
                  className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${
                    e.warranty.state === "expired" ? "bg-red-100 text-red-700" :
                    e.warranty.state === "expiring_soon" ? "bg-amber-100 text-amber-700" :
                    "bg-emerald-100 text-emerald-700"
                  }`}
                  title={`Installed ${e.warranty.install_date} • ${e.warranty.warranty_years}yr warranty • expires ${e.warranty.expires_on}`}>
                  {e.warranty.state === "expired" ? "Warranty Expired" :
                    e.warranty.state === "expiring_soon" ? `Warranty ${e.warranty.days_left}d` :
                    "Under Warranty"}
                </div>
              )}
            </div>
            <div className="md:col-span-1 text-xs font-bold">{e.refrigerant_type}</div>
            <div className="md:col-span-2">
              <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${ACTION_COLORS[e.action] || "bg-slate-200"}`}>
                {ACTION_LABELS[e.action] || e.action}
              </span>
            </div>
            <div className="md:col-span-1 md:text-right font-black">{parseFloat(e.lbs).toFixed(2)}</div>
            <div className="md:col-span-2 flex items-center gap-2 justify-between">
              <div className="text-xs text-slate-600 truncate">{e.reason || "—"}</div>
              <button onClick={() => remove(e.id)} data-testid={`refrig-delete-${e.id}`}
                className="text-slate-400 hover:text-red-600 p-1 flex-shrink-0">
                <Trash size={14}/>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
